import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ChevronDown, Handshake, Briefcase, LogIn } from 'lucide-react';

/**
 * Thin secondary horizontal navigation bar (Feb 2026 — Founder brief).
 *
 * Renders directly below `PublicHeader`, spans the full viewport width, and
 * does NOT overlap the left/right sidebars because it lives in the section
 * that precedes the 3-column layout. Light-grey background with navy text
 * per the design brief. Horizontally scrollable on narrow viewports so
 * every item stays reachable without a hamburger.
 */
const ITEMS = [
  { key: 'features',       label: 'Features',       to: '/#features' },
  { key: 'for-buyers',     label: 'For Buyers',     to: '/#for-buyers' },
  { key: 'for-suppliers',  label: 'For Suppliers',  to: '/#for-suppliers' },
  { key: 'partners',       label: 'Partners',       dropdown: true },
  { key: 'pricing',        label: 'Pricing',        to: '/pricing' },
  { key: 'about',          label: 'About Us',       to: '/#trust' },
  { key: 'contact',        label: 'Contact',        to: '/#contact' },
];

export default function PublicSubNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [openPartners, setOpenPartners] = useState(false);
  const partnersRef = useRef(null);

  // Close the Partners dropdown on outside-click / Escape.
  useEffect(() => {
    if (!openPartners) return;
    const onClick = (e) => {
      if (partnersRef.current && !partnersRef.current.contains(e.target)) {
        setOpenPartners(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpenPartners(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [openPartners]);

  const go = (to) => {
    if (to?.startsWith('/#')) {
      const hash = to.slice(1);
      if (location.pathname !== '/') { navigate('/' + hash); return; }
      const el = document.getElementById(hash.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate(to);
  };

  return (
    <nav
      className="border-b border-slate-200"
      style={{ backgroundColor: '#F4F7FB' }}
      aria-label="Secondary navigation"
      data-testid="public-sub-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ul className="flex items-center flex-wrap gap-1 text-sm font-semibold" style={{ color: '#082A4D' }}>
          {ITEMS.map((item) => {
            if (item.dropdown) {
              return (
                <li key={item.key} className="relative" ref={partnersRef}>
                  <button
                    type="button"
                    onClick={() => setOpenPartners((o) => !o)}
                    aria-haspopup="menu"
                    aria-expanded={openPartners}
                    className={
                      'px-3 py-2.5 inline-flex items-center gap-1 border-b-2 transition-colors duration-150 ' +
                      (openPartners
                        ? 'text-[#D4AF37] border-[#D4AF37]'
                        : 'text-[#082A4D] border-transparent hover:text-[#D4AF37] hover:border-[#D4AF37]')
                    }
                    data-testid="subnav-partners-toggle"
                  >
                    {item.label}
                    <ChevronDown className={'w-3.5 h-3.5 transition-transform ' + (openPartners ? 'rotate-180' : '')} />
                  </button>
                  {openPartners && (
                    <div
                      className="absolute top-full left-0 mt-1 z-30 min-w-[240px] bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden"
                      role="menu"
                      data-testid="subnav-partners-menu"
                    >
                      <Link
                        to="/partner-register"
                        onClick={() => setOpenPartners(false)}
                        className="flex items-start gap-2 px-3 py-2.5 hover:bg-slate-50 border-b border-slate-100"
                        role="menuitem"
                        data-testid="subnav-partner-register"
                      >
                        <Handshake className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
                        <div>
                          <div className="text-xs font-bold text-[#082A4D]">Become a Referral Partner</div>
                          <div className="text-[10px] font-normal text-slate-500">Earn commission on referred societies.</div>
                        </div>
                      </Link>
                      <Link
                        to="/dsa-register"
                        onClick={() => setOpenPartners(false)}
                        className="flex items-start gap-2 px-3 py-2.5 hover:bg-slate-50 border-b border-slate-100"
                        role="menuitem"
                        data-testid="subnav-dsa-register"
                      >
                        <Briefcase className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
                        <div>
                          <div className="text-xs font-bold text-[#082A4D]">Become a DSA</div>
                          <div className="text-[10px] font-normal text-slate-500">Direct Selling Agents — dedicated pipeline.</div>
                        </div>
                      </Link>
                      <Link
                        to="/login"
                        onClick={() => setOpenPartners(false)}
                        className="flex items-start gap-2 px-3 py-2.5 hover:bg-slate-50"
                        role="menuitem"
                        data-testid="subnav-partner-login"
                      >
                        <LogIn className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
                        <div>
                          <div className="text-xs font-bold text-[#082A4D]">Partner / DSA Login</div>
                          <div className="text-[10px] font-normal text-slate-500">Access your existing dashboard.</div>
                        </div>
                      </Link>
                    </div>
                  )}
                </li>
              );
            }
            return (
              <li key={item.key}>
                <button
                  type="button"
                  onClick={() => go(item.to)}
                  className="px-3 py-2.5 border-b-2 border-transparent text-[#082A4D] hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors duration-150"
                  data-testid={`subnav-${item.key}`}
                >
                  {item.label}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
